import { Box } from '@chakra-ui/react';
import { Section } from '../../../Finger/Section';
import FingerWithAction, { Status } from '../../../atoms/finger-with-action';
import {
  EducationSponsorshipMap,
  FamilySponsorshipMap,
  ICourse,
  IOtherName,
  IOtherNationality,
  IRegionConfig,
  IRisk,
  IStudentVisa,
  OrganisationSponsorshipMap,
  OtherSponsorshipMap,
  OtherVisa,
  ScholarshipSponsorshipMap,
  VisaRefusal,
} from '../../../../types';

import NameAndDOB from '../../../sections/personal_info/name_and_dob';
import NationalityAndSex from '../../../sections/personal_info/nationality_and_sex';
import ContactAndAddress from '../../../sections/personal_info/contact_and_address';
import CriminalConvictionAndDisability from '../../../sections/personal_info/criminal_convictions_and_disability';
import CourseDetailsAndDeposit from '../../../sections/personal_info/course_details_and_deposit';
import CourseOfferEvidence from '../../../sections/personal_info/course_offer_evidence';
import PassportDetails from '../../../sections/personal_info/passport_details';
import EnglishTestPI from '../../../sections/personal_info/english_test';
import EnrolmentSupport from '../../../sections/personal_info/enrolment_support';
import PriorStudyAndTravel from '../../../sections/history/prior_study_and_travel';
import PriorTravelAndRefusals from '../../../sections/history/prior_travel_and_refusals';
import UpcomingVisaAndDependants from '../../../sections/history/upcoming_visa_and_dependants';
import Finances from '../../../sections/finance';
import AgentDetailsSummary from '../../../sections/agent_details';
import VisaSupport from '../../../sections/personal_info/visa_support';
import WhatsApp from '../../../sections/personal_info/whatsApp';
import { UserRole } from '../../../../store/auth/types';
import { hasPerm, P } from '../../../../utilities/roles/permissions';
import { SectionStatusType, CourseLevel, EUSSStatus } from 'api-paths-cas';
import { hasNoRisks, isAllRisksCleared } from '../../../../utilities/risks';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { selectIntakeAfterCutoff } from '../../../../store/selectors';

import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/studentLeftNav/types';
import LockedSectionWarning from '../../../molecules/locked-section-warning';
import CASBypassWarning from '../../../molecules/casBypassWarning/casBypassWarning';
import { useParams } from 'react-router-dom';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';

export interface IDispatchProps {
  onUpdateRisks: (source: string, clearedRisks: any) => void;
}

export interface IProps {
  statusArray: Array<{
    finger: string;
    title?: string;
    complete: boolean;
    risk: boolean;
  }>;
  historySectionStatus: SectionStatusType;
  financeSectionStatus: SectionStatusType;
  givenName?: string;
  middleName?: string;
  surname?: string;
  hasOtherNames: string;
  otherNames?: Array<IOtherName>;
  dob?: string;
  nationality?: string;
  appliedForEUSS?: string;
  grantedEUSSStatus?: EUSSStatus;
  applyingFromHomeCountry: string;
  countryOfVisaApplication?: string;
  livedInUKLast12Months?: boolean;
  otherCountryReason?: string;
  hasOtherNationality: string;
  otherNationalities?: Array<IOtherNationality>;
  countryOfBirth?: string;
  gender?: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  postCode?: string;
  country?: string;
  hasCriminalConvictions: string;
  criminalConvictionReason?: string;
  studentNo?: string;
  courses?: Array<ICourse>;
  courseFeeDepositReqd: string;
  courseFeeDeposit: string;
  courseFeeDepositDate: string;
  courseFeeDepositNotReqdReason: string;
  accomDepositReqd: string;
  accomDeposit: string;
  accomDepositDate: string;
  hasEnglishTest: string;
  englishTestType?: string;
  englishTestTypeOther?: string;
  englishTestNotReqdReason?: string;
  englishTestNotReqdReasonOther?: string;
  qualification?: string;
  qualificationOther?: string;
  passportStatus?: string;
  passportExpiry?: string;
  passportExpected?: string;
  withAgent?: string;
  visaSupport?: string;
  useWhatsapp?: string;
  whatsAppPhoneNumber?: string;

  lastCourse?: string;
  lastCourseOther?: string;
  lastCourseYear?: string;
  hasOverseasTravel: string;
  overseasTravelTimes?: string;
  hasUkTravel: string;
  ukTravelTimes?: string;
  hasPreviousUkStudyVisa: string;
  hasPreviousUkStudy: string;
  previousUkStudyVisas?: Array<IStudentVisa>;
  lastCourseLevel?: CourseLevel;
  lowerOrSameLevelReason?: string;

  hasOtherUkVisas?: string;
  otherUkVisas?: Array<OtherVisa>;
  hasLeaveToRemain?: string;
  hasBorderRefusal?: string;
  borderRefusalReason?: string;
  hasUkVisaRefusals?: string;
  ukVisaRefusals?: Array<OtherVisa>;
  hasOtherVisaRefusals?: string;
  otherVisaRefusals?: Array<VisaRefusal>;
  hasDependents: string;
  hasPartner: string;
  partnerVisaStatus: string;
  hasChildren: string;
  childrenNum: string;

  organisationSponsorship: OrganisationSponsorshipMap;
  scholarshipSponsorship: ScholarshipSponsorshipMap;
  educationSponsorship: EducationSponsorshipMap;
  familySponsorship: FamilySponsorshipMap;
  otherSponsorship: OtherSponsorshipMap;
  riskList: IRisk;
  userRole: UserRole;
  personalInfoTitle: string;
  yourHistoryTitle: string;
  consentsTitle: string;
  financialTitle: string;
  isBeginCasRequest?: boolean;

  config: IRegionConfig;
}

type Props = IProps & IDispatchProps;

function Checklist(props: Props) {
  const { offerId = '' } = useParams();

  const { navigateTo } = useCustomNavigate();
  const onResume = (url: string) => navigateTo(`${offerId}/${url}`);

  const handleActionClick = (target: string) => {
    switch (target) {
      case 'PIP1':
        onResume('personal-info/step-1');
        break;
      case 'PIP2':
        onResume('personal-info/step-2');
        break;
      case 'PIP3':
        onResume('personal-info/step-3');
        break;
      case 'PIP4':
        onResume('personal-info/step-4');
        break;
      case 'PIP5':
        onResume('personal-info/step-5');
        break;
      case 'PIP5_1':
        onResume('personal-info/step-5-1');
        break;
      case 'PIP6':
        onResume('personal-info/step-6');
        break;
      case 'PIP7':
        onResume('personal-info/step-7');
        break;
      case 'PIP8':
        onResume('personal-info/step-8');
        break;
      case 'PIP9':
        onResume('personal-info/step-9');
        break;
      case 'PIP10':
        onResume('personal-info/step-10');
        break;
      case 'HP1':
        onResume('history/step-1');
        break;
      case 'HP2':
        onResume('history/step-2');
        break;
      case 'HP3':
        onResume('history/step-3');
        break;
      case 'CP1':
        onResume('consents/step-1');
        break;
      case 'CP2':
        onResume('consents/step-2');
        break;
      case 'CP3':
        onResume('consents/step-3');
        break;
      case 'CP4':
        onResume('consents/step-4');
        break;
      case 'FP1':
        onResume('finances/step-1');
        break;
      case 'AD1':
        onResume('agent-details/step-1');
        break;
    }
  };

  const isCasBypass = useSelector(({ student }: ApplicationState) => student.offer.casBypass);
  const offer = useSelector(({ student }: ApplicationState) => student.offer);
  const isSectionLocked = useIsSectionLocked(SectionId.CHECKLIST, offer.status);
  const agentDetailsEnabled =
    useSelector(({ student }: ApplicationState) => student.config?.agentDetailsEnabled) !== false;
  const intakeAfterCutoff = useSelector(selectIntakeAfterCutoff);
  const agentSectionVisible = offer.withAgent && agentDetailsEnabled && intakeAfterCutoff;

  const handleUpdateRisksClick = (target: string, source: string) => {
    let clearedRisks = {};
    switch (target) {
      case 'PIP1':
        clearedRisks = {
          personalInfoRisk: {
            namesAndAliases: props.riskList.personalInfoRisk.namesAndAliases || [],
          },
        };
        break;
      case 'PIP2':
        clearedRisks = {
          personalInfoRisk: {
            birthAndNationality: props.riskList.personalInfoRisk.birthAndNationality || [],
          },
        };
        break;
      case 'PIP4':
        clearedRisks = {
          personalInfoRisk: {
            criminalConvictions: props.riskList.personalInfoRisk.criminalConvictions || [],
          },
        };
        break;
      case 'PIP6':
        clearedRisks = {
          personalInfoRisk: {
            englishTest: props.riskList.personalInfoRisk.englishTest || [],
          },
        };
        break;
      case 'PIP7':
        clearedRisks = {
          personalInfoRisk: {
            passportDetails: props.riskList.personalInfoRisk.passportDetails || [],
          },
        };
        break;
      case 'HP1':
        clearedRisks = {
          historyRisk: {
            priorStudyAndTravel: props.riskList.historyRisk.priorStudyAndTravel || [],
          },
        };
        break;
      case 'HP2':
        clearedRisks = {
          historyRisk: {
            priorStudyAndRefusals: props.riskList.historyRisk.priorStudyAndRefusals || [],
          },
        };
        break;
      case 'HP3':
        clearedRisks = {
          historyRisk: {
            travellersAndDependants: props.riskList.historyRisk.travellersAndDependants || [],
          },
        };
        break;
      case 'FP1':
        clearedRisks = { fundingRisk: props.riskList.fundingRisk || {} };
        break;
    }
    props.onUpdateRisks(source, clearedRisks);
  };

  const checkCheckedOffRisks = (target: string): boolean => {
    const clearedRisks = props.riskList?.clearedRisks ?? {};
    switch (target) {
      case 'PIP1':
        return (clearedRisks.personalInfoRisk && clearedRisks.personalInfoRisk?.namesAndAliases?.length > 0) || false;
      case 'PIP2':
        return (
          (clearedRisks.personalInfoRisk && clearedRisks.personalInfoRisk?.birthAndNationality?.length > 0) || false
        );
      case 'PIP4':
        return (
          (clearedRisks.personalInfoRisk && clearedRisks.personalInfoRisk?.criminalConvictions?.length > 0) || false
        );
      case 'PIP6':
        return (clearedRisks.personalInfoRisk && clearedRisks.personalInfoRisk?.englishTest?.length > 0) || false;
      case 'PIP7':
        return (clearedRisks.personalInfoRisk && clearedRisks.personalInfoRisk?.passportDetails?.length > 0) || false;
      case 'HP1':
        return (clearedRisks.historyRisk && clearedRisks.historyRisk?.priorStudyAndTravel?.length > 0) || false;
      case 'HP2':
        return (clearedRisks.historyRisk && clearedRisks.historyRisk?.priorStudyAndRefusals?.length > 0) || false;
      case 'HP3':
        return (clearedRisks.historyRisk && clearedRisks.historyRisk?.travellersAndDependants?.length > 0) || false;
      case 'FP1':
        const FClearedRisks = clearedRisks.fundingRisk;
        const FRisks = props.riskList?.fundingRisk;
        if (!FRisks) return true;

        if (FClearedRisks) {
          if (
            !isAllRisksCleared(null, FRisks.educationSponsorship, FClearedRisks.educationSponsorship) ||
            !isAllRisksCleared(null, FRisks.familySponsorship, FClearedRisks.familySponsorship) ||
            !isAllRisksCleared(null, FRisks.organisationSponsorship, FClearedRisks.organisationSponsorship) ||
            !isAllRisksCleared(null, FRisks.scholarshipSponsorship, FClearedRisks.scholarshipSponsorship) ||
            !isAllRisksCleared(null, FRisks.other, FClearedRisks.other)
          ) {
            return false;
          }
        } else {
          return (
            hasNoRisks(FRisks.educationSponsorship) &&
            hasNoRisks(FRisks.familySponsorship) &&
            hasNoRisks(FRisks.organisationSponsorship) &&
            hasNoRisks(FRisks.scholarshipSponsorship) &&
            hasNoRisks(FRisks.other)
          );
        }
        return true;
      default:
        return false;
    }
  };

  const canResume = hasPerm(props.userRole, P.CHECKLIST_EDIT);
  const showRisks = hasPerm(props.userRole, P.CHECKLIST_FLAGS);
  const canCheckOffRisks = hasPerm(props.userRole, P.CHECK_OFF_RISKS);

  function getSummary(target: string) {
    switch (target) {
      case 'PIP1':
        return (
          <NameAndDOB
            showRisks={showRisks}
            givenName={props.givenName}
            middleName={props.middleName}
            surname={props.surname}
            hasOtherNames={props.hasOtherNames}
            otherNames={props.otherNames}
            riskList={props.riskList?.personalInfoRisk?.namesAndAliases}
          />
        );
      case 'PIP2':
        return (
          <NationalityAndSex
            showRisks={showRisks}
            nationality={props.nationality}
            countryOfBirth={props.countryOfBirth}
            gender={props.gender}
            appliedForEUSS={props.appliedForEUSS}
            grantedEUSSStatus={props.grantedEUSSStatus}
            applyingFromHomeCountry={props.applyingFromHomeCountry}
            countryOfVisaApplication={props.countryOfVisaApplication}
            livedInUKLast12Months={props.livedInUKLast12Months}
            otherCountryReason={props.otherCountryReason}
            hasOtherNationality={props.hasOtherNationality}
            otherNationalities={props.otherNationalities}
            dob={props.dob}
            riskList={props.riskList?.personalInfoRisk?.birthAndNationality}
          />
        );
      case 'PIP3':
        return (
          <ContactAndAddress
            showRisks={showRisks}
            phoneNumber={props.phoneNumber}
            address={props.address}
            city={props.city}
            postcode={props.postCode}
            country={props.country}
          />
        );
      case 'PIP4':
        return (
          <CriminalConvictionAndDisability
            showRisks={showRisks}
            hasCriminalConvictions={props.hasCriminalConvictions}
            criminalConvictionReason={props.criminalConvictionReason}
            riskList={props.riskList?.personalInfoRisk?.criminalConvictions}
          />
        );
      case 'PIP5':
        return <CourseDetailsAndDeposit studentNo={props.studentNo} courses={props.courses} />;
      case 'PIP5_1':
        return <CourseOfferEvidence showRisks={showRisks} courses={props.courses} />;
      case 'PIP6':
        return (
          <EnglishTestPI
            showRisks={showRisks}
            hasEnglishTest={props.hasEnglishTest}
            englishTestType={props.englishTestType}
            englishTestTypeOther={props.englishTestTypeOther}
            englishTestNotReqdReason={props.englishTestNotReqdReason}
            englishTestNotReqdReasonOther={props.englishTestNotReqdReasonOther}
            qualification={props.qualification}
            qualificationOther={props.qualificationOther}
            riskList={props.riskList?.personalInfoRisk?.englishTest}
          />
        );
      case 'PIP7':
        return (
          <PassportDetails
            showRisks={showRisks}
            passportStatus={props.passportStatus}
            passportExpiry={props.passportExpiry}
            passportExpected={props.passportExpected}
            riskList={props.riskList?.personalInfoRisk?.passportDetails}
          />
        );
      case 'PIP8':
        return <EnrolmentSupport showRisks={showRisks} withAgent={props.withAgent} />;
      case 'PIP9':
        return <VisaSupport config={props.config} showRisks={showRisks} visaSupport={props.visaSupport} />;
      case 'PIP10':
        return (
          <WhatsApp
            showRisks={showRisks}
            useWhatsapp={props.useWhatsapp}
            whatsAppPhoneNumber={props.whatsAppPhoneNumber}
          />
        );
      case 'HP1':
        return (
          <PriorStudyAndTravel
            showRisks={showRisks}
            lastCourse={props.lastCourse}
            lastCourseOther={props.lastCourseOther}
            lastCourseYear={props.lastCourseYear}
            hasOverseasTravel={props.hasOverseasTravel}
            overseasTravelTimes={props.overseasTravelTimes}
            hasUkTravel={props.hasUkTravel}
            ukTravelTimes={props.ukTravelTimes}
            hasPreviousUkStudy={props.hasPreviousUkStudy}
            hasPreviousUkStudyVisa={props.hasPreviousUkStudyVisa}
            previousUkStudyVisas={props.previousUkStudyVisas}
            lastCourseLevel={props.lastCourseLevel}
            lowerOrSameLevelReason={props.lowerOrSameLevelReason}
            riskList={props.riskList?.historyRisk?.priorStudyAndTravel}
          />
        );
      case 'HP2':
        return (
          <PriorTravelAndRefusals
            showRisks={showRisks}
            hasOtherUkVisas={props.hasOtherUkVisas}
            otherUkVisas={props.otherUkVisas}
            hasLeaveToRemain={props.hasLeaveToRemain}
            hasBorderRefusal={props.hasBorderRefusal}
            borderRefusalReason={props.borderRefusalReason}
            hasUkVisaRefusals={props.hasUkVisaRefusals}
            ukVisaRefusals={props.ukVisaRefusals}
            hasOtherVisaRefusals={props.hasOtherVisaRefusals}
            otherVisaRefusals={props.otherVisaRefusals}
            riskList={props.riskList?.historyRisk?.priorStudyAndRefusals}
          />
        );
      case 'HP3':
        return (
          <UpcomingVisaAndDependants
            showRisks={showRisks}
            hasDependents={props.hasDependents}
            hasPartner={props.hasPartner}
            partnerVisaStatus={props.partnerVisaStatus}
            hasChildren={props.hasChildren}
            childrenNum={props.childrenNum}
            riskList={props.riskList?.historyRisk?.travellersAndDependants}
          />
        );

      case 'FP1':
        return (
          <Finances
            showRisks={showRisks}
            organisationSponsorship={props.organisationSponsorship}
            scholarshipSponsorship={props.scholarshipSponsorship}
            educationSponsorship={props.educationSponsorship}
            familySponsorship={props.familySponsorship}
            otherSponsorship={props.otherSponsorship}
            risk={props.riskList}
            courseFeeDepositReqd={props.courseFeeDepositReqd}
            courseFeeDeposit={props.courseFeeDeposit}
            courseFeeDepositDate={props.courseFeeDepositDate}
            courseFeeDepositNotReqdReason={props.courseFeeDepositNotReqdReason}
            accomDepositReqd={props.accomDepositReqd}
            accomDeposit={props.accomDeposit}
            accomDepositDate={props.accomDepositDate}
          />
        );
      default:
        return null;
    }
  }

  const isAgentOrUni = [
    UserRole.AgencyAdmin,
    UserRole.Agent,
    UserRole.UniAdmin,
    UserRole.Uni,
    UserRole.UniLite,
    UserRole.UniSecure,
  ].includes(props.userRole);

  const isAgent = useSelector(
    ({ auth }: ApplicationState) => auth.role === UserRole.Agent || auth.role === UserRole.AgencyAdmin,
  );

  return (
    <>
      <Box>
        {isCasBypass ? (
          <CASBypassWarning />
        ) : (
          <>
            {isSectionLocked && <LockedSectionWarning />}
            <Section label={props.personalInfoTitle} id={'personalInfo'}>
              {props.statusArray
                .filter(page => page.finger.startsWith('PIP'))
                .map((finger, index) => (
                  <FingerWithAction
                    isBeginCasRequest={isAgentOrUni && index === 0}
                    id={finger.finger}
                    key={finger.finger}
                    status={
                      finger.risk
                        ? Status.AtRisk
                        : finger.complete
                          ? Status.Complete
                          : props.statusArray.indexOf(finger) > 0
                            ? props.statusArray[props.statusArray.indexOf(finger) - 1].complete
                              ? Status.InProgress
                              : Status.Todo
                            : Status.InProgress
                    }
                    label={finger.title!}
                    isCheckedOff={checkCheckedOffRisks(finger.finger)}
                    onClick={() => handleActionClick(finger.finger)}
                    onCheckOffRisk={() => handleUpdateRisksClick(finger.finger, finger.title!)}
                    showAction={canResume}
                    canCheckOff={canCheckOffRisks}
                    noEdit={isSectionLocked && isAgent}
                  >
                    <>{getSummary(finger.finger)}</>
                  </FingerWithAction>
                ))}
            </Section>

            {props.historySectionStatus === SectionStatusType.ACTIVE && (
              <Section label={props.yourHistoryTitle} id={'history'}>
                {props.statusArray
                  .filter(page => page.finger.startsWith('HP'))
                  .map(finger => (
                    <FingerWithAction
                      id={finger.finger}
                      key={finger.finger}
                      status={
                        finger.risk
                          ? Status.AtRisk
                          : finger.complete
                            ? Status.Complete
                            : props.statusArray.indexOf(finger) > 0
                              ? props.statusArray[props.statusArray.indexOf(finger) - 1].complete
                                ? Status.InProgress
                                : Status.Todo
                              : Status.InProgress
                      }
                      label={finger.title ?? ''}
                      isCheckedOff={checkCheckedOffRisks(finger.finger)}
                      onClick={() => handleActionClick(finger.finger)}
                      onCheckOffRisk={() => handleUpdateRisksClick(finger.finger, finger.title!)}
                      showAction={canResume}
                      canCheckOff={canCheckOffRisks}
                      noEdit={isSectionLocked && isAgent}
                    >
                      <>{getSummary(finger.finger)}</>
                    </FingerWithAction>
                  ))}
              </Section>
            )}

            <Section label={props.consentsTitle} id={'consents'}>
              {props.statusArray
                .filter(page => page.finger.startsWith('CP'))
                .map(finger => (
                  <FingerWithAction
                    id={finger.finger}
                    key={finger.finger}
                    status={
                      finger.risk
                        ? Status.AtRisk
                        : finger.complete
                          ? Status.Complete
                          : props.statusArray.indexOf(finger) > 0
                            ? props.statusArray[props.statusArray.indexOf(finger) - 1].complete
                              ? Status.InProgress
                              : Status.Todo
                            : Status.InProgress
                    }
                    label={finger.title ?? ''}
                    onClick={() => handleActionClick(finger.finger)}
                    showAction={canResume}
                    noEdit={isSectionLocked && isAgent}
                  />
                ))}
            </Section>

            {props.financeSectionStatus === SectionStatusType.ACTIVE && (
              <Section label={props.financialTitle} id={'financials'}>
                {props.statusArray
                  .filter(page => page.finger.startsWith('FP'))
                  .map(finger => (
                    <FingerWithAction
                      id={finger.finger}
                      key={finger.finger}
                      status={
                        finger.risk
                          ? Status.AtRisk
                          : finger.complete
                            ? Status.Complete
                            : props.statusArray.indexOf(finger) > 0
                              ? props.statusArray[props.statusArray.indexOf(finger) - 1].complete
                                ? Status.InProgress
                                : Status.Todo
                              : Status.InProgress
                      }
                      label={finger.title ?? ''}
                      isCheckedOff={checkCheckedOffRisks(finger.finger)}
                      onClick={() => handleActionClick(finger.finger)}
                      onCheckOffRisk={() => handleUpdateRisksClick(finger.finger, finger.title!)}
                      showAction={canResume}
                      canCheckOff={canCheckOffRisks}
                      noEdit={isSectionLocked && isAgent}
                    >
                      <>{getSummary(finger.finger)}</>
                    </FingerWithAction>
                  ))}
              </Section>
            )}

            {agentSectionVisible && (
              <Section label="Agent Details" id={'agentDetails'}>
                <FingerWithAction
                  id="AD1"
                  status={offer.isAgentConfirmed && offer.isAgentDetailsConfirmed ? Status.Complete : Status.InProgress}
                  label="Agent Details"
                  onClick={() => handleActionClick('AD1')}
                  showAction={canResume}
                  noEdit={isSectionLocked && isAgent}
                >
                  <AgentDetailsSummary
                    displayName={offer.assignedAgents?.[0]?.displayName ?? ''}
                    importAgentName={
                      offer.importAgentId === undefined || offer.importAgentId === offer.assignedAgents?.[0]?.id
                        ? (offer.importAgentName ?? undefined)
                        : undefined
                    }
                    agentFamilyName={offer.agentFamilyName ?? ''}
                    agentGivenName={offer.agentGivenName ?? ''}
                    branchAddressLine1={offer.branchAddressLine1 ?? ''}
                    branchAddressLine2={offer.branchAddressLine2 ?? ''}
                    branchAddressLine3={offer.branchAddressLine3 ?? ''}
                    branchCity={offer.branchCity ?? ''}
                    branchCountry={offer.branchCountry ?? ''}
                    branchPostcode={offer.branchPostcode ?? ''}
                  />
                </FingerWithAction>
              </Section>
            )}
          </>
        )}
      </Box>
    </>
  );
}

export default Checklist;
